import { useEffect, useRef, type ReactNode } from "react";

interface DialogProps {
  titleId: string;
  onClose: () => void;
  children: ReactNode;
  /** "modal" (centralizado) ou "drawer" (lateral). */
  variant?: "modal" | "drawer";
  wide?: boolean;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Overlay acessivel compartilhado por Modal e Drawer: Escape fecha, foco
 * preso dentro do dialogo (Tab/Shift+Tab), foco inicial no dialogo e foco
 * devolvido ao elemento que abriu ao fechar. */
export function Dialog({ titleId, onClose, children, variant = "modal", wide }: DialogProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    containerRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !containerRef.current) return;

      const focusables = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      previouslyFocused.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // O backdrop fechar no clique-fora e um atalho de MOUSE apenas: o
    // equivalente por teclado (Escape, tratado acima, e o botao de fechar
    // dentro do dialogo) ja cobre o mesmo caso de uso, entao este div nao
    // precisa entrar na ordem de tab nem responder a tecla.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={
          variant === "drawer" ? "drawer" : `modal${wide ? " modal--wide" : ""}`
        }
      >
        {children}
      </div>
    </div>
  );
}
